/**
 * Form component for ticket revenue line items
 */

import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Typography,
  InputAdornment,
  Box,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import type { TicketRevenueItem, TicketRevenueType } from '../../../types/TicketRevenueItem';
import { formatCurrency } from '../../../utils/formatters';

interface TicketRevenueItemFormProps {
  item: TicketRevenueItem;
  onChange: (item: TicketRevenueItem) => void;
  onRemove: () => void;
  disabled?: boolean;
}

export const TicketRevenueItemForm: React.FC<TicketRevenueItemFormProps> = ({
  item,
  onChange,
  onRemove,
  disabled = false,
}) => {
  const subtotal = item.quantity * item.price;

  const handleChange = (field: keyof TicketRevenueItem, value: string | number) => {
    onChange({
      ...item,
      [field]: value,
    });
  };

  return (
    <Box sx={{ mb: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Grid container spacing={2} alignItems="center">
        {/* Type */}
        <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>Type</InputLabel>
            <Select
              value={item.type}
              label="Type"
              onChange={(e) => handleChange('type', e.target.value as TicketRevenueType)}
            >
              <MenuItem value="presale">Pre-sale</MenuItem>
              <MenuItem value="door">Door</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Name */}
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <TextField
            label="Name"
            placeholder="Ticket Name"
            value={item.name}
            onChange={(e) => handleChange('name', e.target.value)}
            size="small"
            fullWidth
            disabled={disabled}
          />
        </Grid>

        {/* Quantity */}
        <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
          <TextField
            label="Quantity"
            type="number"
            value={item.quantity}
            onChange={(e) => handleChange('quantity', parseInt(e.target.value) || 0)}
            size="small"
            fullWidth
            disabled={disabled}
            inputProps={{ min: 0 }}
          />
        </Grid>

        {/* Price */}
        <Grid size={{ xs: 6, sm: 4, lg: 2 }}>
          <TextField
            label="Price"
            type="number"
            value={item.price}
            onChange={(e) => handleChange('price', parseFloat(e.target.value) || 0)}
            size="small"
            fullWidth
            disabled={disabled}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>

        {/* Subtotal - Right aligned */}
        <Grid size={{ xs: 10, sm: 3, lg: 2 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Subtotal
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              {formatCurrency(subtotal)}
            </Typography>
          </Box>
        </Grid>

        {/* Delete button */}
        <Grid size={{ xs: 2, sm: 1, lg: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <IconButton
              color="error"
              size="small"
              aria-label={`Remove ticket ${item.name || 'line item'}`}
              onClick={onRemove}
              disabled={disabled}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
